import React, { useState } from 'react';
import { Package, X, Plus, Minus } from 'lucide-react';
import type { Product, ProductStatus, UnitType, ProductSpecification } from '@/types/product';
import { VendorLinkSection } from './VendorLinkSection';
import { getStoredCategories } from '../../../../utils/categoryStorage';

interface ProductFormProps {
  product?: Product | null;
  onSubmit: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => void;
  onCancel: () => void;
}

const STATUSES: ProductStatus[] = ['active', 'discontinued', 'out-of-stock'];
const UNITS: UnitType[] = ['piece', 'box', 'kg', 'liter', 'set'];

export function ProductForm({ product, onSubmit, onCancel }: ProductFormProps) {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    sku: product?.sku || '',
    description: product?.description || '',
    category: product?.category || '',
    status: product?.status || 'active',
    basePrice: product?.basePrice?.toString() || '',
    currentStock: product?.currentStock?.toString() || '',
    minimumStock: product?.minimumStock?.toString() || '',
    unit: product?.unit || 'piece',
    specifications: product?.specifications || [],
    images: product?.images || [],
    dimensions: product?.dimensions || {
      length: 0,
      width: 0,
      height: 0,
      unit: 'cm',
    },
    weight: product?.weight || {
      value: 0,
      unit: 'kg',
    },
  });

  const [newSpec, setNewSpec] = useState({ key: '', value: '' });
  const [categories] = useState(() => getStoredCategories());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      basePrice: parseFloat(formData.basePrice),
      currentStock: parseInt(formData.currentStock, 10),
      minimumStock: parseInt(formData.minimumStock, 10),
      specifications: formData.specifications,
      images: formData.images,
      attachments: [],
    });
  };

  const addSpecification = () => {
    if (newSpec.key && newSpec.value) {
      setFormData(prev => ({
        ...prev,
        specifications: [...prev.specifications, newSpec],
      }));
      setNewSpec({ key: '', value: '' });
    }
  };

  const removeSpecification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col max-h-[80vh]">
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center space-x-2">
          <Package className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-800">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Product Name *</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">SKU *</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.sku}
              onChange={e => setFormData(prev => ({ ...prev, sku: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Category *</label>
            <select
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.category || ''}
              onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="">Select category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status *</label>
            <select
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.status}
              onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as ProductStatus }))}
            >
              {STATUSES.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Base Price *</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={formData.basePrice}
                onChange={e => setFormData(prev => ({ ...prev, basePrice: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Unit *</label>
            <select
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.unit}
              onChange={e => setFormData(prev => ({ ...prev, unit: e.target.value as UnitType }))}
            >
              {UNITS.map(unit => (
                <option key={unit} value={unit}>
                  {unit.charAt(0).toUpperCase() + unit.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Current Stock *</label>
            <input
              type="number"
              required
              min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.currentStock}
              onChange={e => setFormData(prev => ({ ...prev, currentStock: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Minimum Stock *</label>
            <input
              type="number"
              required
              min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.minimumStock}
              onChange={e => setFormData(prev => ({ ...prev, minimumStock: e.target.value }))}
            />
          </div>
        </div>

        {product?.id && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Vendor Associations</h3>
            <VendorLinkSection productId={product.id} />
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specifications</label>
            <div className="space-y-2">
              {formData.specifications.map((spec, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="flex-1 text-sm">{spec.key}: {spec.value}</span>
                  <button
                    type="button"
                    onClick={() => removeSpecification(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex space-x-2">
              <input
                type="text"
                placeholder="Key"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={newSpec.key}
                onChange={e => setNewSpec(prev => ({ ...prev, key: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Value"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={newSpec.value}
                onChange={e => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
              />
              <button
                type="button"
                onClick={addSpecification}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
            <div className="space-y-2">
              {formData.images.map((image, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <img src={image} alt="" className="h-10 w-10 rounded object-cover" />
                  <span className="flex-1 text-sm truncate">{image}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== index),
                      }));
                    }}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <input
                type="url"
                placeholder="Enter image URL"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const input = e.target as HTMLInputElement;
                    if (input.value) {
                      setFormData(prev => ({
                        ...prev,
                        images: [...prev.images, input.value],
                      }));
                      input.value = '';
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 sticky bottom-0">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {product ? 'Update Product' : 'Add Product'}
        </button>
      </div>
    </form>
  );
}